import React, { useEffect, useMemo, useState } from "react";
import {
  FaBroadcastTower,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaExclamationTriangle,
  FaHistory,
  FaLayerGroup,
  FaPlayCircle,
  FaSyncAlt,
  FaTv,
} from "react-icons/fa";
import TvEmptyState from "../../components/tv-disply/TvEmptyState";
import {
  TV_DELIVERY_STATUSES,
  TV_PUBLISH_REQUEST_STATUSES,
  TV_PUBLISH_TARGET_MODES,
} from "../../services/tv-disply/tvDisplayConstants";
import {
  getComputedDeliveryStatus,
  publishTvPlaylist,
  validatePlaylistForPublish,
} from "../../services/tv-disply/tvPublishService";

export default function TvPublishing({
  playlistRows = [],
  deviceRows = [],
  publicationRows = [],
}) {
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [validation, setValidation] = useState(null);
  const [validationStatus, setValidationStatus] = useState(TV_PUBLISH_REQUEST_STATUSES.IDLE);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [publishResult, setPublishResult] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [targetMode, setTargetMode] = useState(TV_PUBLISH_TARGET_MODES.ALL);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  const selectedPlaylist = useMemo(
    () => playlistRows.find((row) => row.id === selectedPlaylistId) || null,
    [playlistRows, selectedPlaylistId]
  );

  const eligibleDevices = useMemo(
    () => deviceRows.filter((device) => device.enabled !== false && device.pairingStatus === "PAIRED"),
    [deviceRows]
  );

  const deliveryRows = useMemo(
    () =>
      deviceRows.map((device) => ({
        ...device,
        deliveryStatus: getComputedDeliveryStatus(device),
      })),
    [deviceRows]
  );

  const deliveryStats = useMemo(() => ({
    published: deliveryRows.filter((row) => row.lastPublishedVersion > 0).length,
    pending: deliveryRows.filter((row) => row.deliveryStatus === TV_DELIVERY_STATUSES.UPDATE_PENDING).length,
    upToDate: deliveryRows.filter((row) => row.deliveryStatus === TV_DELIVERY_STATUSES.UP_TO_DATE).length,
    waiting: deliveryRows.filter((row) => row.deliveryStatus === TV_DELIVERY_STATUSES.WAITING_FOR_TV).length,
    error: deliveryRows.filter((row) => row.deliveryStatus === TV_DELIVERY_STATUSES.ERROR).length,
  }), [deliveryRows]);

  useEffect(() => {
    if (selectedPlaylistId && playlistRows.some((row) => row.id === selectedPlaylistId)) return;
    setSelectedPlaylistId(playlistRows[0]?.id || "");
  }, [playlistRows, selectedPlaylistId]);

  useEffect(() => {
    if (!selectedPlaylistId) {
      setValidation(null);
      setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.IDLE);
      return;
    }

    let active = true;
    setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.VALIDATING);
    validatePlaylistForPublish({ playlistId: selectedPlaylistId, targetMode: TV_PUBLISH_TARGET_MODES.ALL })
      .then((result) => {
        if (!active) return;
        setValidation(result);
        setValidationStatus(result.ready ? TV_PUBLISH_REQUEST_STATUSES.READY : TV_PUBLISH_REQUEST_STATUSES.ERROR);
      })
      .catch((validationError) => {
        if (!active) return;
        setValidation(null);
        setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.ERROR);
        setError(validationError?.message || "Unable to validate this playlist for publication.");
      });

    return () => {
      active = false;
    };
  }, [selectedPlaylistId]);

  function openPublishModal(playlistId) {
    setSelectedPlaylistId(playlistId);
    setTargetMode(TV_PUBLISH_TARGET_MODES.ALL);
    setSelectedDeviceIds([]);
    setPublishResult(null);
    setFeedback("");
    setError("");
    setPublishModalOpen(true);
  }

  async function handlePublish() {
    if (!selectedPlaylist) return;

    setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.PUBLISHING);
    setError("");
    setFeedback("");
    setPublishResult(null);

    try {
      const result = await publishTvPlaylist({
        playlistId: selectedPlaylist.id,
        targetMode,
        deviceIds: targetMode === TV_PUBLISH_TARGET_MODES.SELECTED ? selectedDeviceIds : undefined,
        requestId: `publish_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      });
      setPublishResult(result);
      setFeedback(`Published version ${result.version} for ${selectedPlaylist.name}.`);
      setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.SUCCESS);
      setPublishModalOpen(false);
    } catch (publishError) {
      setError(publishError?.message || "Publishing failed.");
      setValidationStatus(TV_PUBLISH_REQUEST_STATUSES.ERROR);
    }
  }

  return (
    <section className="tv-display-panel" id="tv-display-publishing">
      <div className="tv-display-panel-head">
        <div>
          <h2 className="tv-display-panel-title">Publishing</h2>
          <p className="tv-display-panel-copy">
            Publish immutable playlist snapshots to one, many, or all eligible TVs. Draft playlists stay private,
            and TVs later listen only to their own `published/current` pointer.
          </p>
        </div>
      </div>

      <div className="tv-display-media-kpi-grid is-five">
        <MetricCard label="Published TVs" value={String(deliveryStats.published)} icon={<FaBroadcastTower />} />
        <MetricCard label="Update Pending" value={String(deliveryStats.pending)} icon={<FaSyncAlt />} />
        <MetricCard label="Up To Date" value={String(deliveryStats.upToDate)} icon={<FaCheckCircle />} />
        <MetricCard label="Waiting For TV" value={String(deliveryStats.waiting)} icon={<FaTv />} />
        <MetricCard label="Recent Publications" value={String(publicationRows.length)} icon={<FaHistory />} />
      </div>

      {feedback ? <div className="tv-display-feedback is-success">{feedback}</div> : null}
      {error ? <div className="tv-display-feedback is-error">{error}</div> : null}

      <div className="tv-display-publishing-grid">
        <div className="tv-display-publishing-column">
          <div className="tv-display-device-section-head">
            <div>
              <div className="tv-display-section-kicker">Ready Playlists</div>
              <h3 className="tv-display-device-section-title">Draft-to-publication handoff</h3>
            </div>
          </div>

          {playlistRows.length ? (
            <div className="tv-display-publication-list">
              {playlistRows.map((playlist) => {
                const publishLabel = getPlaylistPublishLabel(playlist);
                return (
                  <article key={playlist.id} className="tv-display-publication-card">
                    <div className="tv-display-publication-head">
                      <div>
                        <div className="tv-display-device-name">{playlist.name}</div>
                        <div className="tv-display-device-id">{playlist.itemCount} items</div>
                      </div>
                      <StatusChip value={publishLabel} tone={publishLabel === "PUBLISHED" ? "success" : "warning"} />
                    </div>
                    <div className="tv-display-publication-meta">
                      <div><strong>Mode:</strong> {playlist.presentation?.displayMode || "MIXED_PLAYLIST"}</div>
                      <div><strong>Orientation:</strong> {playlist.presentation?.orientation || "LANDSCAPE"}</div>
                      <div><strong>Last Published:</strong> {playlist.lastPublishedVersion ? `Version ${playlist.lastPublishedVersion}` : "Never"}</div>
                      <div><strong>Targets:</strong> {playlist.lastPublishedTargetCount || 0} TVs</div>
                    </div>
                    <div className="tv-display-card-actions">
                      <button
                        type="button"
                        className="tv-display-action-btn is-primary"
                        onClick={() => openPublishModal(playlist.id)}
                      >
                        <FaCloudUploadAlt />
                        {playlist.lastPublishedVersion ? "Publish New Version" : "Publish to TV"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <TvEmptyState
              title="No playlists available"
              copy="Create a draft playlist first, then return here to validate and publish an immutable version."
            />
          )}
        </div>

        <div className="tv-display-publishing-column">
          <div className="tv-display-device-section-head">
            <div>
              <div className="tv-display-section-kicker">Publish Readiness</div>
              <h3 className="tv-display-device-section-title">Server-validated before publish</h3>
            </div>
          </div>

          {selectedPlaylist ? (
            <article className="tv-display-publication-card">
              <div className="tv-display-publication-head">
                <div>
                  <div className="tv-display-device-name">{selectedPlaylist.name}</div>
                  <div className="tv-display-device-id">
                    {validationStatus === TV_PUBLISH_REQUEST_STATUSES.VALIDATING ? "Validating..." : "Server-side readiness check"}
                  </div>
                </div>
                <button type="button" className="tv-display-action-btn" onClick={() => openPublishModal(selectedPlaylist.id)}>
                  <FaPlayCircle />
                  Publish
                </button>
              </div>

              {validation ? (
                <div className="tv-display-readiness-list">
                  {validation.checks?.map((item) => (
                    <div key={item} className="tv-display-readiness-line is-good">
                      <FaCheckCircle />
                      <span>{item}</span>
                    </div>
                  ))}
                  {validation.blockingIssues?.map((item) => (
                    <div key={item} className="tv-display-readiness-line is-bad">
                      <FaExclamationTriangle />
                      <span>{item}</span>
                    </div>
                  ))}
                  <div className="tv-display-publication-meta">
                    <div><strong>Playable Items:</strong> {validation.playableItemCount || 0}</div>
                    <div><strong>Eligible TVs:</strong> {validation.eligibleDeviceCount || eligibleDevices.length}</div>
                  </div>
                </div>
              ) : null}
            </article>
          ) : (
            <TvEmptyState
              title="Select a playlist"
              copy="Readiness checks appear here after a playlist is available for validation."
            />
          )}

          <div className="tv-display-device-section-head">
            <div>
              <div className="tv-display-section-kicker">Recent Publications</div>
              <h3 className="tv-display-device-section-title">Immutable publication history</h3>
            </div>
          </div>

          {publicationRows.length ? (
            <div className="tv-display-publication-list">
              {publicationRows.slice(0, 8).map((publication) => (
                <article key={publication.id} className="tv-display-publication-card">
                  <div className="tv-display-publication-head">
                    <div>
                      <div className="tv-display-device-name">{publication.playlistName}</div>
                      <div className="tv-display-device-id">
                        {publication.publicationId} · Version {publication.version}
                      </div>
                    </div>
                    <StatusChip value={publication.status} tone={publication.status === "READY" ? "success" : "warning"} />
                  </div>
                  <div className="tv-display-publication-meta">
                    <div><strong>Targets:</strong> {publication.successfulTargetCount}/{publication.targetCount || publication.successfulTargetCount + publication.failedTargetCount}</div>
                    <div><strong>Scope:</strong> {publication.scope}</div>
                    <div><strong>Items:</strong> {publication.itemCount}</div>
                    <div><strong>Published:</strong> {formatDateTime(publication.publishedAt)}</div>
                  </div>
                  {publication.failures?.length ? (
                    <div className="tv-display-warning-list">
                      {publication.failures.map((failure) => (
                        <div key={`${publication.id}_${failure.deviceId}`} className="tv-display-warning-line">
                          <FaExclamationTriangle />
                          <span>{failure.deviceId}: {failure.message}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          ) : (
            <TvEmptyState
              title="No publications yet"
              copy="Once a playlist is published, immutable version history appears here."
            />
          )}
        </div>
      </div>

      <div className="tv-display-device-section-head">
        <div>
          <div className="tv-display-section-kicker">Device Delivery</div>
          <h3 className="tv-display-device-section-title">Published vs applied versions</h3>
        </div>
      </div>

      {deliveryRows.length ? (
        <div className="tv-display-device-list">
          {deliveryRows.map((device) => (
            <article key={device.id} className="tv-display-device-record">
              <div className="tv-display-device-record-head">
                <div className="tv-display-device-record-title">
                  <div className="tv-display-device-icon"><FaLayerGroup /></div>
                  <div>
                    <div className="tv-display-device-name">{device.deviceName || device.deviceId}</div>
                    <div className="tv-display-device-id">{device.deviceId}</div>
                  </div>
                </div>
                <div className="tv-display-device-badges">
                  <StatusChip value={device.deliveryStatus} tone={toneForDelivery(device.deliveryStatus)} />
                </div>
              </div>
              <div className="tv-display-device-record-grid">
                <DetailCard label="Assigned" value={device.assignedPlaylistName || "Not Assigned"} />
                <DetailCard label="Published" value={device.lastPublishedVersion ? `Version ${device.lastPublishedVersion}` : "Never"} />
                <DetailCard label="Applied" value={device.lastAppliedVersion != null ? `Version ${device.lastAppliedVersion}` : "Waiting"} />
                <DetailCard label="Last Sync Error" value={device.lastSyncError || "None"} />
              </div>
            </article>
          ))}
        </div>
      ) : (
        <TvEmptyState
          title="No paired TVs yet"
          copy="Pair at least one TV before monitoring delivery status."
        />
      )}

      {publishModalOpen && selectedPlaylist ? (
        <PublishModal
          playlist={selectedPlaylist}
          eligibleDevices={eligibleDevices}
          targetMode={targetMode}
          setTargetMode={setTargetMode}
          selectedDeviceIds={selectedDeviceIds}
          setSelectedDeviceIds={setSelectedDeviceIds}
          onClose={() => setPublishModalOpen(false)}
          onPublish={handlePublish}
          busy={validationStatus === TV_PUBLISH_REQUEST_STATUSES.PUBLISHING}
        />
      ) : null}

      {publishResult ? (
        <div className="tv-display-feedback is-success">
          Published Version {publishResult.version}. Successful: {publishResult.successfulTargetCount} TVs. Failed: {publishResult.failedTargetCount}.
        </div>
      ) : null}
    </section>
  );
}

function PublishModal({
  playlist,
  eligibleDevices,
  targetMode,
  setTargetMode,
  selectedDeviceIds,
  setSelectedDeviceIds,
  onClose,
  onPublish,
  busy,
}) {
  function toggleDevice(deviceId) {
    setSelectedDeviceIds((current) =>
      current.includes(deviceId) ? current.filter((entry) => entry !== deviceId) : [...current, deviceId]
    );
  }

  const publishDisabled =
    busy ||
    !eligibleDevices.length ||
    (targetMode === TV_PUBLISH_TARGET_MODES.SELECTED && !selectedDeviceIds.length);

  return (
    <div className="tv-display-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="tv-display-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="tv-display-modal-head">
          <h3>Publish Playlist</h3>
          <button type="button" className="tv-display-modal-close" onClick={onClose}>Close</button>
        </div>

        <div className="tv-display-publication-meta">
          <div><strong>{playlist.name}</strong></div>
          <div>{playlist.itemCount} items</div>
        </div>

        {!eligibleDevices.length ? (
          <div className="tv-display-feedback is-error">No paired and enabled TVs are available. Pair a TV first.</div>
        ) : null}

        <div className="tv-display-target-mode-list">
          <label className="tv-display-target-choice">
            <input
              type="radio"
              checked={targetMode === TV_PUBLISH_TARGET_MODES.ALL}
              onChange={() => setTargetMode(TV_PUBLISH_TARGET_MODES.ALL)}
            />
            <span>All Eligible TVs</span>
          </label>
          <label className="tv-display-target-choice">
            <input
              type="radio"
              checked={targetMode === TV_PUBLISH_TARGET_MODES.SELECTED}
              onChange={() => setTargetMode(TV_PUBLISH_TARGET_MODES.SELECTED)}
            />
            <span>Selected TVs</span>
          </label>
        </div>

        {targetMode === TV_PUBLISH_TARGET_MODES.SELECTED ? (
          <div className="tv-display-target-picker">
            <div className="tv-display-card-actions">
              <button type="button" className="tv-display-action-btn" onClick={() => setSelectedDeviceIds(eligibleDevices.map((device) => device.deviceId))}>
                Select All Eligible TVs
              </button>
              <button type="button" className="tv-display-action-btn" onClick={() => setSelectedDeviceIds([])}>
                Clear Selection
              </button>
            </div>

            {eligibleDevices.map((device) => (
              <label key={device.id} className="tv-display-target-row">
                <input
                  type="checkbox"
                  checked={selectedDeviceIds.includes(device.deviceId)}
                  onChange={() => toggleDevice(device.deviceId)}
                />
                <div>
                  <div className="tv-display-device-name">{device.deviceName || device.deviceId}</div>
                  <div className="tv-display-device-id">
                    {device.deviceId} · {device.computedStatus}
                  </div>
                </div>
              </label>
            ))}
          </div>
        ) : null}

        <div className="tv-display-feedback is-loading">
          Publish "{playlist.name}" to {targetMode === TV_PUBLISH_TARGET_MODES.ALL ? eligibleDevices.length : selectedDeviceIds.length} TVs? A new immutable version will be created.
        </div>

        <div className="tv-display-modal-actions">
          <button type="button" className="tv-display-action-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="tv-display-action-btn is-primary" onClick={onPublish} disabled={publishDisabled}>
            {busy ? "Publishing..." : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon }) {
  return (
    <div className="tv-display-media-kpi-card">
      <div className="tv-display-media-kpi-label">{label}</div>
      <div className="tv-display-media-kpi-value">{value}</div>
      <div className="tv-display-device-id">{icon}</div>
    </div>
  );
}

function DetailCard({ label, value }) {
  return (
    <div className="tv-display-device-meta-card">
      <div className="tv-display-device-meta-label">{label}</div>
      <div className="tv-display-device-meta-value">{value}</div>
    </div>
  );
}

function StatusChip({ value, tone = "" }) {
  const chipTone = tone || String(value || "").toLowerCase();
  return <span className={`tv-display-chip is-${chipTone}`}>{value}</span>;
}

function toneForDelivery(status) {
  if (status === TV_DELIVERY_STATUSES.UP_TO_DATE) return "success";
  if (status === TV_DELIVERY_STATUSES.ERROR) return "danger";
  if (status === TV_DELIVERY_STATUSES.NEVER_PUBLISHED) return "muted";
  return "warning";
}

function getPlaylistPublishLabel(playlist) {
  if (playlist.hasUnpublishedChanges && playlist.lastPublishedVersion) return "CHANGES NOT PUBLISHED";
  if (playlist.lastPublishedVersion) return "PUBLISHED";
  return "DRAFT";
}

function formatDateTime(value) {
  if (!value) return "Not yet";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);
  if (!Number.isFinite(date.getTime())) return "Not yet";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
